<template>
  <div class="py-1">
    <template v-if="hasEnterResultsAction && canUser('orders.enterResults')">
      <MenuOption
        id="enter-results"
        :title="enterResultsActionLabel"
        @select="onEnterResultsSelect">
        <template #icon>
          <IconLucideFlaskConical class="h-4 w-4" />
        </template>
      </MenuOption>
      <MenuDivider />
    </template>
    <MenuOption
      v-if="
        analysisStatus === 'received' &&
        !isReportLocked &&
        canUser('orders.enterResults')
      "
      id="edit-completed-results"
      :title="$t('W: Rediger afsluttede resultater')"
      @select="onEditCompletedResultsSelect">
      <template #icon>
        <IconLucidePencil class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="analysisStatus !== 'cancelled' && !isReportLocked && canUser('orders.cancel')"
      id="cancel"
      :title="$t('W: Annuller analyse')"
      @select="onCancelSelect">
      <template #icon>
        <IconLucideX class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="analysisStatus === 'cancelled' && !isReportLocked && canUser('orders.cancel')"
      id="uncancel"
      :title="$t('W: Genaktiver analyse')"
      @select="onUncancelSelect">
      <template #icon>
        <IconLucideRotateCcw class="h-4 w-4" />
      </template>
    </MenuOption>
  </div>
</template>

<script lang="ts">
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useUserStore } from '@/stores/userStore'
import type { TOrderSampleAnalysisStatus } from '@team-milva/lab-models/resources'
import { mapActions } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'

export default {
  name: 'OrderSampleAnalysisMenu',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    sampleID: { type: String, required: true },
    analysisID: { type: String, required: true },
    analysisStatus: {
      type: String as () => TOrderSampleAnalysisStatus,
      required: true,
    },
    isReportLocked: { type: Boolean, default: false },
    hasEnterResultsAction: { type: Boolean, default: false },
  },

  computed: {
    enterResultsActionLabel(): string {
      if (this.analysisStatus === 'awaiting-qa') {
        return this.$t('W: Gennemgå')
      }
      return ['started', 'received-partially'].includes(this.analysisStatus)
        ? this.$t('action.continue')
        : this.$t('W: Angiv resultater')
    },
  },

  methods: {
    ...mapActions(useModalStore, ['open']),
    ...mapActions(useUserStore, ['canUser']),
    ...mapActions(useOrderStore, ['cancelAnalysis', 'uncancelAnalysis']),

    onEnterResultsSelect(): void {
      this.$emit('close')
      this.open({
        name: 'EnterAnalysisResultsModal',
        width: 'md',
        useEasyClose: false,
        props: {
          orderID: this.orderID,
          sampleID: this.sampleID,
          analysisID: this.analysisID,
        },
      })
    },

    onEditCompletedResultsSelect(): void {
      this.$emit('close')
      this.open({
        name: 'EnterAnalysisResultsModal',
        width: 'md',
        useEasyClose: false,
        props: {
          orderID: this.orderID,
          sampleID: this.sampleID,
          analysisID: this.analysisID,
          isCompleted: true,
        },
      })
    },

    onCancelSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Annuller analyse'),
          textLines: [
            this.$t('W: Er du sikker på, at du vil annullere denne analyse?'),
          ],
          onConfirm: async () => {
            try {
              await this.cancelAnalysis(this.orderID, this.sampleID, this.analysisID)
              useToast().success(this.$t('W: Analyse annulleret'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke annullere analyse'))
            }
          },
        },
      })
    },

    onUncancelSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Genaktiver analyse'),
          textLines: [
            this.$t('W: Er du sikker på, at du vil genaktivere denne analyse?'),
          ],
          onConfirm: async () => {
            try {
              await this.uncancelAnalysis(this.orderID, this.sampleID, this.analysisID)
              useToast().success(this.$t('W: Analyse genaktiveret'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke genaktivere analyse'))
            }
          },
        },
      })
    },
  },
}
</script>

